import { Formio } from "formiojs";
import Field from "formiojs/components/_classes/field/Field";
import _ from "lodash";

import AJAX from "../../functions/ajax";
import formatBytes from "../../vendors/formatBytes";

const Uppy = require("@uppy/core");
const Tus = require("@uppy/tus");
const FileInput = require("@uppy/file-input");
const ProgressBar = require("@uppy/progress-bar");

// Small upload button for review forms. Shares the tus backend with the
// submitter's Uppy Dashboard, but registers under its own type - `uppy` is
// already taken by BaseFormio.jsx and formiojs' registry is global.
class CustomFileUploadComponent extends Field {
  static schema(...extend) {
    return Field.schema(
      {
        type: "review_upload",
        label: "Upload",
        key: "review_upload",
        storage: "url",
        dir: "review",
        buttonLabel: "Datei hochladen",
        removeLabel: "Datei entfernen",
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "File Upload",
      icon: "upload",
      group: "basic",
      weight: 80,
      schema: CustomFileUploadComponent.schema(),
    };
  }

  get defaultSchema() {
    return CustomFileUploadComponent.schema();
  }

  get emptyValue() {
    return [];
  }

  // tus endpoint, file-json and download URLs, put on the schema root by
  // ReviewCollectionCaseView. Absent when the reviewer may not edit.
  get uploadConfig() {
    return this.root?._form?.uploadConfig || {};
  }

  get isReadOnly() {
    return !!(this.options.readOnly || this.disabled || this.component.disabled);
  }

  get maxFiles() {
    const max = this.component.config?.upload?.restrictions?.maxNumberOfFiles;
    return _.isNumber(max) && max > 0 ? max : null;
  }

  // Uppy counts the files it currently holds, and we remove each one as soon as
  // its json has been fetched - so the cap it gets is the slots still free.
  uppyRestrictions() {
    const restrictions = this.component.config?.upload?.restrictions || {};
    const types = (restrictions.allowedFileTypes || []).filter(type => type && type !== "all");
    const max = this.maxFiles;

    return {
      maxNumberOfFiles: max === null ? null : Math.max(max - this.files().length, 0),
      maxFileSize: restrictions.maxFileSize || null,
      allowedFileTypes: types.length ? types : null,
    };
  }

  files() {
    return Array.isArray(this.dataValue) ? this.dataValue.slice() : [];
  }

  renderFiles() {
    return this.files()
      .map((file, index) => this.renderFile(file, index))
      .join("");
  }

  renderFile(file, index) {
    const size = _.isNumber(file.size) ? formatBytes(file.size) : "";

    return `
      <div class="reviewUpload__item">
        <a class="reviewUpload__name"
           href="${_.escape(file.url || "")}"
           target="_blank"
           rel="noopener noreferrer"
           download>${_.escape(file.name || "")}</a>
        <span class="reviewUpload__size">${size}</span>
        ${
          this.isReadOnly
            ? ""
            : `<button type="button" class="reviewUpload__remove" data-index="${index}" aria-label="${this.t(
                this.component.removeLabel
              )}">&times;</button>`
        }
      </div>
    `;
  }

  render() {
    return super.render(`
      <div class="reviewUpload" ref="wrapper">
        <div class="reviewUpload__list" ref="list">${this.renderFiles()}</div>
        ${
          this.isReadOnly
            ? ""
            : `<div class="reviewUpload__upload" ref="upload">
                 <div ref="button"></div>
                 <div class="reviewUpload__progress" ref="progress"></div>
               </div>`
        }
        <div class="reviewUpload__err" ref="error"></div>
      </div>
    `);
  }

  attach(element) {
    this.loadRefs(element, {
      wrapper: "single",
      list: "single",
      upload: "single",
      button: "single",
      progress: "single",
      error: "single",
    });

    this.attachFileList();

    if (!this.isReadOnly) {
      this.initUppy();
      this.syncControls();
    }

    return super.attach(element);
  }

  detach() {
    this.closeUppy();
    return super.detach();
  }

  initUppy() {
    const { tus } = this.uploadConfig;
    if (!tus || !this.refs.button) return;

    const tusConfig = _.cloneDeep(tus);
    // Merged into the OriginalFile's meta.source next to the header the backend
    // put on the schema, so the row records which field it was uploaded for.
    tusConfig.headers = {
      ...tusConfig.headers,
      "upload-source": JSON.stringify({
        key: this.component.key,
        dir: this.component.dir,
      }),
    };

    this.uppy = Uppy({
      id: `review-upload-${this.id}`,
      autoProceed: true,
      allowMultipleUploads: true,
      restrictions: this.uppyRestrictions(),
    })
      .use(Tus, tusConfig)
      .use(FileInput, {
        target: this.refs.button,
        pretty: true,
        locale: { strings: { chooseFiles: this.t(this.component.buttonLabel) } },
      })
      .use(ProgressBar, { target: this.refs.progress, hideAfterFinish: true })
      .on("upload-success", this.onUploadSuccess)
      .on("upload-error", this.onUploadError)
      .on("restriction-failed", (file, error) => this.showError(error.message));
  }

  closeUppy() {
    if (this.uppy) {
      this.uppy.close();
      this.uppy = null;
    }
  }

  onUploadSuccess = (file, response) => {
    const id = _.last(
      String(response?.uploadURL || "")
        .split("/")
        .filter(Boolean)
    );
    const url = id && this.uploadConfig.fileJsonUrl?.replace("{id}", id);

    if (!url) {
      this.showError(`${file.name}: ${this.t("Upload failed")}`);
      return;
    }

    return AJAX()
      .get(file.id, url)
      .then(res => {
        if (!res?.data) throw new Error(this.t("Upload failed"));
        this.addFile(res.data);
        if (this.uppy) this.uppy.removeFile(file.id);
      })
      .catch(e => this.showError(`${file.name}: ${e && e.message}`));
  };

  onUploadError = (file, error) => {
    const match = (error.message || "").match(/response code: (.+?), response text: ([\s\S]+)\)$/m);
    const [code, respMsg] = match ? match.slice(1) : ["500", ""];
    this.showError(
      `${file.name} (#${code}: ${code !== "500" ? respMsg : "Internal server error"})`
    );
  };

  addFile(file) {
    const files = this.files();
    files.push(file);
    this.setFiles(files);
  }

  removeFile(index) {
    const files = this.files();
    if (!(index >= 0) || index >= files.length) return;

    files.splice(index, 1);
    this.setFiles(files);
  }

  setFiles(files) {
    this.updateValue(files, { modified: true });
    this.hideError();
    this.refreshFiles();
    this.triggerChange();
  }

  // Redrawing instead would tear down Uppy mid-upload, so only the list is
  // rebuilt - and its remove buttons rebound.
  refreshFiles() {
    if (!this.refs.list) return;

    this.refs.list.innerHTML = this.renderFiles();
    this.attachFileList();
    this.syncControls();
  }

  attachFileList() {
    if (!this.refs.list) return;

    this.refs.list.querySelectorAll(".reviewUpload__remove").forEach(button => {
      this.addEventListener(button, "click", event => {
        event.preventDefault();
        this.removeFile(Number(button.dataset.index));
      });
    });
  }

  syncControls() {
    const restrictions = this.uppyRestrictions();

    if (this.uppy) {
      this.uppy.setOptions({ restrictions });
    }
    if (this.refs.upload) {
      this.refs.upload.classList.toggle(
        "reviewUpload__upload--full",
        restrictions.maxNumberOfFiles === 0
      );
    }
  }

  showError(message) {
    if (this.refs.error) {
      this.refs.error.textContent = message || "";
    }
  }

  hideError() {
    this.showError("");
  }

  getValue() {
    return this.files();
  }

  setValue(value, flags = {}) {
    const changed = super.setValue(this.normalizeValue(value), flags);
    this.refreshFiles();
    return changed;
  }

  normalizeValue(value) {
    if (!value) return this.emptyValue;

    const files = Array.isArray(value) ? value : [value];
    return files.filter(file => _.isPlainObject(file));
  }

  isEmpty(value = this.dataValue) {
    return !Array.isArray(value) || value.length === 0;
  }
}

Formio.registerComponent("review_upload", CustomFileUploadComponent);

export default CustomFileUploadComponent;
